import React, { useMemo } from 'react';
import LineWrapper from './LineWrapper';
import NumericCard from './cards/NumericCard';
import {
  defaultResponseHandler,
  formatMs,
  Grid,
  useUserinfo,
} from '@mss/mss-component-library';
import Skeleton from '../../common/Skeleton';
import { MappedStatus, mapStatus, Status } from '../util';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Fetch from '../../common/util/_fetch';
import { useLicensesQuery } from '../../asset/services/queries';

type Props = {
  endpoint: string;
  defaultValue: Status;
  permissions: string[];
};

const StatusLine = ({ endpoint, permissions, defaultValue }: Props) => {
  const { t } = useTranslation();
  const [hasPermission] = useUserinfo();
  const { data: license } = useLicensesQuery();

  const defaultStatus = useMemo(
    () => ({ ...mapStatus(defaultValue, t), status: defaultValue }),
    [defaultValue]
  );

  const hasLicense = () => {
    if (!defaultStatus.licenseKey) return true;
    return !!license?.[defaultStatus.licenseKey];
  };

  const { data, isLoading } = useQuery<MappedStatus>({
    queryKey: ['status', endpoint],
    queryFn: async () => {
      const resp = await defaultResponseHandler<Status>({
        t,
        request: Fetch.get(`/api/status/${endpoint}/`),
      });
      const { data: status, error } = resp;
      status?.cards?.sort(a => (a.type === 'total' ? -1 : 1));
      return error
        ? defaultStatus
        : ({ ...mapStatus(status, t), status } as MappedStatus);
    },
    enabled: hasPermission(permissions) && hasLicense(),
    cacheTime: formatMs({ minutes: 15 }),
    staleTime: formatMs({ minutes: 5 }),
    refetchInterval: formatMs({ minutes: 1 }),
  });

  const {
    name,
    cards,
    totalKey = 'total',
    ChartComponent,
    TableComponent,
    colors,
    tooltips,
    status,
    areAssets,
  } = data ?? defaultStatus;

  if (!hasPermission(permissions) || !hasLicense()) return null;
  return (
    <Grid.Row>
      <Grid.Col width={12}>
        {isLoading && <Skeleton className="h-12 w-20" />}
        {!isLoading && cards && (
          <h1 className="font-semibold text-2xl">
            {name} -
            {` ${cards.find(x => x.type === totalKey)?.count} ${
              totalKey ?? 'total'
            }`}
          </h1>
        )}
      </Grid.Col>
      <Grid.Col width={12}>
        <div className="flex">
          <div className="basis-4/12">
            <div className="h-[264px] w-full pl-4 border-gray-200 border bg-full-white flex items-center mb-2 mr-2">
              {ChartComponent && colors && (
                <ChartComponent
                  loading={isLoading}
                  status={status!}
                  colors={colors}
                />
              )}
              {!ChartComponent && <Skeleton className="h-full w-full" />}
            </div>
          </div>
          <LineWrapper>
            {status.cards.map(({ type: cardType, count, url }, i) => (
              <NumericCard
                color={colors?.[i - 1]}
                key={i}
                tooltip={tooltips?.[cardType] ?? ''}
                label={cardType}
                groupName={name ?? ''}
                number={count}
                loading={isLoading}
                tableComponent={TableComponent ?? (() => <></>)}
                url={url}
                areAssets={areAssets}
              />
            ))}
          </LineWrapper>
        </div>
      </Grid.Col>
    </Grid.Row>
  );
};

export default StatusLine;
